import { Box, css, Stack, Typography, useTheme } from '@mui/material'
import { setMonth } from 'date-fns'
import type { UiState } from 'instantsearch.js'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'next-i18next'
import { useContext, useMemo, useRef } from 'react'
import {
  Configure,
  InstantSearch,
  InstantSearchServerState,
  InstantSearchSSRProvider,
  Stats,
  useInstantSearch
} from 'react-instantsearch'
import { match } from 'ts-pattern'
import Breadcrumb from 'components/Breadcrumb'
import { CurrencyExchangeRateContext } from 'components/Hook/CurrencyExchangeRateContext'
import BreadcrumbMicrodata from 'components/Microdata/Breadcrumb'
import ListMicrodata from 'components/Microdata/SEOPageList'
import Pagination from 'components/Pagination'
import { SeoLink } from 'components/SeoLink'
import { LocaleContext } from 'components/common/LocaleContext'
import { NewFlexContainer } from 'components/common/common.style'
import {
  defaultSortBy,
  instantSearchClient,
  searchIndex
} from 'containers/meilisearchClient'
import { ResultsContent } from 'containers/search/Filters/ResultsContent'
import { SortByProvider } from 'containers/search/Filters/SortByContext'
import { SortBy } from 'containers/search/SortBy'
import {
  SeoStatsContainer,
  SeoTitleContainer,
  SpacedDash
} from 'containers/seoPages/index.style'
import defaultStartDate from 'domain/constants/EditionLiveStartDateTimestamp'
import { isNotNullOrUndefined } from 'helpers/array'
import {
  getParsedData,
  LocationType,
  MonthType,
  TypeOrDistanceType
} from 'helpers/index'
import { raceCategoriesLevel1, urlParser } from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'
import regions from 'helpers/regions'
import { EventByRegion } from 'pages/[typeOrDistance]/[location]'
import { unifiedColors } from 'styles/colors'
import { Theme, themeUtils } from 'styles/theme'
import FakeFilters from './FakeFilters'

const capitalize = (s?: string) => s && s[0]?.toUpperCase() + s.slice(1)
const uncapitalize = (s?: string) => s && s.toLowerCase()

const ErrorHandler = () => {
  const { error, status, results } = useInstantSearch()
  const isSearchStalled = status === 'stalled'
  const { t } = useTranslation('common')

  return error || (!isSearchStalled && results.nbHits < 1) ? (
    <div
      css={css`
        & + div {
          display: none;
        }
        margin-top: 50px;
        ${themeUtils.maxWidth.md} {
          padding: 0 16px;
          margin-bottom: 24px;
        }
      `}
    >
      <Typography variant="h3">{t('eventListing.no_found_races')}</Typography>
    </div>
  ) : (
    <></>
  )
}

export interface SeoPagesProps {
  serverState?: InstantSearchServerState
  page?: number
  typeOrDistance?: TypeOrDistanceType
  resultsByRegion?: {
    resultsByRegion?: Record<string, number>
    resultsByLvl1: EventByRegion | Record<string, number>
    resultsByLvl2: EventByRegion | Record<string, number>
  }
  location?: LocationType
  month?: MonthType
  filters?: string
  resultsState?: any
  attributesToRetrieve: Array<string> // Necessary to limit data sent to the client
  currencyExchangeRates: Record<string, number>
}

export default function SeoPages({
  page,
  serverState,
  typeOrDistance,
  resultsByRegion,
  location,
  month,
  filters,
  attributesToRetrieve,
  currencyExchangeRates
}: SeoPagesProps) {
  const debouncedSetStateRef = useRef<NodeJS.Timeout | null>(null)

  const theme: Theme = useTheme()
  const { t, i18n } = useTranslation('common')
  const { t: frenchT } = useTranslation('common', { lng: 'fr' })

  const router = useRouter()
  const { dateStringLocale } = useContext(LocaleContext)

  function handleSearchStateChange(updatedSearchState: UiState[string]) {
    if (debouncedSetStateRef.current) {
      clearTimeout(debouncedSetStateRef.current)
    }

    const { page: updatedPage, sortBy } = updatedSearchState

    const query = { ...(router?.query ? router.query : {}) }

    if (updatedPage === undefined || updatedPage === 1) {
      delete query.page
    } else {
      query.page = `${updatedPage}`
    }

    if (sortBy && sortBy !== defaultSortBy) {
      query.sortBy = sortBy
    }

    debouncedSetStateRef.current = setTimeout(() => {
      router?.replace({ query }, undefined, {
        shallow: true
      })
    }, 400)
  }

  const {
    parsedLocation,
    parsedTypeOrDistance,
    parsedMonth,
    parsedMother,
    parsedChild,
    distances,
    typeOrDistanceKey
  } = getParsedData({
    location,
    typeOrDistance,
    month
  })

  const dateMonth = isNotNullOrUndefined(parsedMonth)
    ? setMonth(new Date(), parsedMonth)
    : null
  const displayMonth = isNotNullOrUndefined(dateMonth)
    ? dateMonth.toLocaleDateString(dateStringLocale, {
        month: 'long'
      })
    : ''

  const finalAtt = {
    ...(attributesToRetrieve && { attributesToRetrieve })
  }

  const breadcrumbData = [
    {
      label: t('eventPage.breadcrumb.allRaces'),
      link: `/run`
    },
    ...(typeOrDistance !== 'run' && parsedMother
      ? [
          {
            label: capitalize(t(parsedMother.key))!,
            link: `/${parsedMother.url}`
          }
        ]
      : []),
    ...(typeOrDistance && typeOrDistanceKey && typeOrDistance !== 'run'
      ? [
          {
            label: capitalize(t(typeOrDistanceKey)),
            link: `/${typeOrDistance}`
          }
        ]
      : []),
    ...(location && location !== 'france'
      ? [
          {
            label: capitalize(parsedLocation)!,
            link: `/${typeOrDistance}/${location}`
          }
        ]
      : []),
    ...(month
      ? [
          {
            label: capitalize(displayMonth)!,
            link: `/${typeOrDistance}/${location}/${month}`
          }
        ]
      : [])
  ]

  const displayLocation = useMemo(() => {
    if (!location) {
      return t('eventListing.inFrance')
    }

    const capitalizedParsedLocation = capitalize(parsedLocation)

    return match(location)
      .with('france', () => t('eventListing.inFrance'))
      .with('la-reunion', () =>
        t('eventListing.nominative_place', {
          location: capitalizedParsedLocation
        })
      )
      .otherwise(() =>
        t('eventListing.place', { location: capitalizedParsedLocation })
      )
  }, [location, parsedLocation, i18n.language])

  const basePath = !router?.isReady
    ? ''
    : `${process.env.APP_URI}${
        router.locale === router.defaultLocale ? '' : `/${router.locale}`
      }`

  const eventsByRegion = resultsByRegion?.resultsByRegion

  return (
    <CurrencyExchangeRateContext.Provider value={currencyExchangeRates}>
      <InstantSearchSSRProvider {...serverState}>
        <InstantSearch
          searchClient={instantSearchClient}
          indexName={searchIndex}
          initialUiState={{
            [searchIndex]: {
              sortBy: defaultSortBy,
              page
            }
          }}
          onStateChange={({ uiState, setUiState }) => {
            if (uiState[searchIndex]) {
              handleSearchStateChange(uiState[searchIndex])
              setUiState(uiState)
            }
          }}
        >
          <SortByProvider>
            <ErrorHandler />
            <NewFlexContainer
              margin="24px 0 106px 0"
              flexDirection="column"
              gap="32px"
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  width: 100%;

                  & .ais-Stats-text {
                    ${themeUtils.body};
                  }
                `}
              >
                <Configure
                  hitsPerPage={42}
                  filters={`editionLiveEndDateTimestamp>${defaultStartDate} AND ${filters}`}
                  {...finalAtt}
                />
                <Stack pl={2}>
                  <Breadcrumb data={breadcrumbData} />
                </Stack>

                <ResultsContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack width="100%">
                      <SeoTitleContainer>
                        <Stack
                          flexDirection="row"
                          gap={2}
                          justifyContent="space-between"
                        >
                          <Typography
                            variant="h1"
                            css={css`
                              ${themeUtils.minWidth.xs} {
                                ${themeUtils.h4}
                              }
                              ${themeUtils.minWidth.md} {
                                ${themeUtils.h2}
                              }
                            `}
                          >
                            {typeOrDistance === 'run'
                              ? t('eventListing.races')
                              : capitalize(t(typeOrDistanceKey!))}
                            {displayLocation}
                            {month && `, ${displayMonth}`}
                          </Typography>
                          <Box display={{ xs: 'none', md: 'block' }}>
                            <Stack
                              flexDirection="row"
                              gap={1}
                              alignItems="center"
                            >
                              <FakeFilters
                                parsedTypeOrDistance={parsedTypeOrDistance}
                                distances={distances}
                                sortBy={defaultSortBy}
                                parsedLocation={parsedLocation}
                              />
                              <SortBy />
                            </Stack>
                          </Box>
                        </Stack>
                        <SeoStatsContainer>
                          <>
                            {typeOrDistance === 'run'
                              ? t('eventListing.races')
                              : t('eventListing.calendar', {
                                  category: uncapitalize(t(typeOrDistanceKey!))
                                })}
                            {displayLocation}
                          </>
                          <SpacedDash />
                          <Stats
                            translations={{
                              rootElementText({ nbHits }) {
                                return nbHits > 100
                                  ? t('eventListing.moreThanHundredHits', {
                                      count: 100
                                    })
                                  : t('eventListing.hit', {
                                      count: nbHits
                                    })
                              }
                            }}
                          />
                        </SeoStatsContainer>
                      </SeoTitleContainer>

                      {typeOrDistance === 'run' && (
                        <Stack
                          flexWrap="wrap"
                          gap={4}
                          rowGap={2}
                          flexDirection="row"
                          pt={1}
                        >
                          {recordKeys(raceCategoriesLevel1).map((type) => {
                            const href = `/${urlParser(
                              raceCategoriesLevel1[type].url
                            )}${location ? `/${location}` : ''}`
                            return (
                              <SeoLink key={type} href={href} label={t(type)} />
                            )
                          })}
                        </Stack>
                      )}

                      {parsedChild && (
                        <div
                          css={css`
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: flex-start;
                            row-gap: 16px;
                            column-gap: 32px;
                            padding-top: 8px;
                          `}
                        >
                          {parsedChild.map((type) => {
                            if (type === '') return null
                            const href = `/${urlParser(frenchT(type))}${
                              location ? `/${location}` : ''
                            }`
                            return (
                              <SeoLink key={type} href={href} label={t(type)} />
                            )
                          })}
                        </div>
                      )}
                      <Box display={{ xs: 'block', md: 'none' }}>
                        <Stack
                          flexDirection="row"
                          gap={1}
                          alignItems="center"
                          justifyContent="space-between"
                          pt={2}
                          width="100%"
                        >
                          <FakeFilters
                            parsedTypeOrDistance={parsedTypeOrDistance}
                            distances={distances}
                            sortBy={defaultSortBy}
                            parsedLocation={parsedLocation}
                          />
                          <SortBy />
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </ResultsContent>
                <Pagination />
              </div>
              <Stack
                p={2}
                borderRadius={3}
                bgcolor={unifiedColors.surface.subdued}
              >
                {/* Le calendrier par région */}
                {eventsByRegion && !parsedLocation && (
                  <div>
                    <h2>
                      {capitalize(t(typeOrDistanceKey!))}
                      {parsedTypeOrDistance ? ' : ' : ''}
                      {t('eventListing.calendar_by_place')}{' '}
                    </h2>

                    <div>
                      <div
                        css={css`
                          display: grid;
                          grid-template-columns: auto auto auto;
                          padding-top: 20px;
                          padding-bottom: 40px;
                          gap: ${theme.spacing(1)};
                          ${themeUtils.maxWidth.md} {
                            grid-template-columns: auto auto;
                          }
                          ${themeUtils.maxWidth.sm} {
                            grid-template-columns: auto;
                          }
                        `}
                      >
                        {recordKeys(eventsByRegion)
                          .sort((a, b) =>
                            a.localeCompare(b, 'fr', {
                              ignorePunctuation: true
                            })
                          )
                          .map((type) => {
                            //TODO: Find a better solution
                            const enforceType = type as keyof typeof regions
                            const region = regions[enforceType]
                            if (eventsByRegion[type]! > 0 && region) {
                              return (
                                <SeoLink
                                  key={region}
                                  href={`/${typeOrDistance}/${region}`}
                                  label={enforceType}
                                  // Counters do not work yet
                                  // counter={
                                  //   resultsByRegion.resultsByRegion[
                                  //     type as keyof typeof resultsByRegion.resultsByRegion
                                  //   ]
                                  // }
                                />
                              )
                            }
                          })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Si Discipline LVL 1 et pas de region */}
                {!location && parsedChild && resultsByRegion?.resultsByLvl1 && (
                  <div
                    css={css`
                      padding-bottom: 25px;
                    `}
                  >
                    <Typography variant="h2">
                      {t('eventListing.other_races_to_try')}
                    </Typography>
                    {typeOrDistance !== 'run' && (
                      <div
                        css={css`
                          display: flex;
                          flex-wrap: wrap;
                          justify-content: flex-start;
                          row-gap: 16px;
                          column-gap: 32px;
                          padding-top: 18px;
                        `}
                      >
                        {recordKeys(raceCategoriesLevel1).map((type) => {
                          if (
                            raceCategoriesLevel1[type].label !==
                            parsedTypeOrDistance
                          )
                            return (
                              <SeoLink
                                key={raceCategoriesLevel1[type].url}
                                href={`/${urlParser(
                                  raceCategoriesLevel1[type].url
                                )}`}
                                label={t(raceCategoriesLevel1[type].key)}
                              />
                            )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Si Discipline LVL 2 et pas de region */}
                {!location && parsedMother && (
                  <div
                    css={css`
                      padding-bottom: 25px;
                    `}
                  >
                    <Typography variant="h2">
                      {t('eventListing.other_categories')}
                    </Typography>
                    {typeOrDistance !== 'run' &&
                      typeOrDistance !== parsedTypeOrDistance &&
                      parsedMother?.level2Categories &&
                      parsedMother.level2Categories.length > 0 && (
                        <div
                          css={css`
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: flex-start;
                            row-gap: 16px;
                            column-gap: 32px;
                            padding-top: 18px;
                          `}
                        >
                          {parsedMother.level2Categories.map((type) => {
                            // Need to find a better solution
                            if (parsedTypeOrDistance !== t(type))
                              return (
                                <SeoLink
                                  key={type}
                                  href={`/${urlParser(frenchT(type))}`}
                                  label={t(type)}
                                />
                              )
                          })}
                        </div>
                      )}
                  </div>
                )}

                {/* Si Discipline LVL 1 et region */}
                {location &&
                  location !== 'france' &&
                  parsedChild &&
                  resultsByRegion?.resultsByLvl1 &&
                  Object.keys(resultsByRegion?.resultsByLvl1).length > 0 && (
                    <div
                      css={css`
                        padding-bottom: 25px;
                      `}
                    >
                      <Typography variant="h2">
                        {t('eventListing.other_types_by_place', {
                          place: parsedLocation
                        })}
                      </Typography>
                      {typeOrDistance !== 'run' && (
                        <div
                          css={css`
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: flex-start;
                            row-gap: 16px;
                            column-gap: 32px;
                            padding-top: 18px;
                          `}
                        >
                          {recordKeys(raceCategoriesLevel1).map((type) => {
                            if (
                              parsedTypeOrDistance &&
                              raceCategoriesLevel1[type].label !==
                                parsedTypeOrDistance &&
                              resultsByRegion.resultsByLvl1[type]
                            )
                              return (
                                <SeoLink
                                  key={raceCategoriesLevel1[type].url}
                                  href={`/${urlParser(
                                    frenchT(type)
                                  )}/${location}`}
                                  label={t(raceCategoriesLevel1[type].key)}
                                />
                              )
                          })}
                        </div>
                      )}
                    </div>
                  )}

                {/* Si Discipline LVL 2 et region */}
                {location &&
                  location !== 'france' &&
                  parsedMother &&
                  resultsByRegion?.resultsByLvl2 && (
                    <div
                      css={css`
                        padding-bottom: 25px;
                      `}
                    >
                      {parsedMother.level2Categories.some(
                        (type) =>
                          parsedTypeOrDistance !== t(type) &&
                          resultsByRegion.resultsByLvl2[type]
                      ) && (
                        <>
                          <Typography variant="h2">
                            {t('eventListing.other_distances_by_place', {
                              place: parsedLocation
                            })}
                          </Typography>
                          {typeOrDistance !== 'run' &&
                            typeOrDistance !== parsedTypeOrDistance &&
                            parsedMother?.level2Categories &&
                            parsedMother.level2Categories.length > 0 && (
                              <div
                                css={css`
                                  display: flex;
                                  flex-wrap: wrap;
                                  justify-content: flex-start;
                                  row-gap: 16px;
                                  column-gap: 32px;
                                  padding-top: 18px;
                                `}
                              >
                                {parsedMother.level2Categories.map((type) => {
                                  // Need to find a better solution
                                  if (
                                    parsedTypeOrDistance !== t(type) &&
                                    resultsByRegion.resultsByLvl2[type]
                                  )
                                    return (
                                      <SeoLink
                                        key={type}
                                        href={`/${urlParser(
                                          frenchT(type)
                                        )}/${location}`}
                                        label={t(type)}
                                      />
                                    )
                                })}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  )}
              </Stack>
            </NewFlexContainer>
            <BreadcrumbMicrodata data={breadcrumbData} basePath={basePath} />
            <ListMicrodata basePath={basePath} />
          </SortByProvider>
        </InstantSearch>
      </InstantSearchSSRProvider>
    </CurrencyExchangeRateContext.Provider>
  )
}
